import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const getIsAllowedOrigin = (referrer: string | null, allowedOrigins: string[]) => {
  if (!referrer) return false;

  try {
    const refOrigin = new URL(referrer).origin;
    return allowedOrigins.some((origin) => {
      return new URL(origin).origin === refOrigin;
    });
  } catch {
    return false;
  }
};

/**
 * Custom hook that returns a `handleBack` function which navigates the user back in history
 * only if the previous page's origin is in the allowed origins list and the browser has history.
 * Otherwise, it navigates to a fallback URL.
 *
 * @param options - Optional configuration object.
 * @param options.allowedOrigins - A list of allowed origins for the referrer. Defaults to environment variables for identity and website.
 * @param options.fallbackUrl - A fallback path to navigate to if conditions aren't met. Defaults to `'/'`.
 * @returns A `handleBack` function to be used in back navigation logic.
 */
export const useBackHandler = (options?: { allowedOrigins?: string[]; fallbackUrl?: string }) => {
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    const referrer = document.referrer;
    const historyLen = window.history.length;

    const { allowedOrigins = [window.REACT_APP_API_BASE_URI, window.REACT_APP_WEBSITE_BASE_URI], fallbackUrl = '/' } = options || {};

    const isAllowedOrigin = getIsAllowedOrigin(referrer, allowedOrigins);

    if (isAllowedOrigin && historyLen > 1) {
      navigate(-1);
    } else {
      navigate(fallbackUrl);
    }
  }, [navigate, options]);

  return handleBack;
};
